import type {
  TransferApprovalRule,
  TransferApprovalRules,
  TransferApprovalTransferType,
} from '~/repositories/PlatformRepository';

const MICROS_PER_DOLLAR = 1_000_000;

export const approvalAmountFromMicros = (amount?: number): string =>
  amount === undefined ? '' : String(amount / MICROS_PER_DOLLAR);

export const approvalAmountToMicros = (amount: string): number | undefined =>
  amount ? Math.round(Number(amount) * MICROS_PER_DOLLAR) : undefined;

export type ApprovalCondition = 'all' | 'disabled' | 'threshold';
export type ConfigurableApprovalRuleKey =
  | 'achCredits'
  | 'achDebits'
  | 'checkDebits'
  | 'internationalWire'
  | 'realtime'
  | 'wire';
export type ApprovalConditions = Partial<Record<ConfigurableApprovalRuleKey, ApprovalCondition>>;
export type ApprovalAmounts = Partial<Record<ConfigurableApprovalRuleKey, number>>;

const APPROVAL_TRANSFER_TYPES: Record<ConfigurableApprovalRuleKey, TransferApprovalTransferType> = {
  achCredits: 'ach_credit',
  achDebits: 'ach_debit',
  checkDebits: 'check_debit',
  internationalWire: 'swift',
  realtime: 'realtime',
  wire: 'wire',
};

const CONFIGURABLE_RULE_KEYS: ConfigurableApprovalRuleKey[] = [
  'achCredits',
  'achDebits',
  'checkDebits',
  'internationalWire',
  'wire',
  'realtime',
];

export const findApprovalRule = (
  rules: TransferApprovalRules | null | undefined,
  key: ConfigurableApprovalRuleKey
): TransferApprovalRule | undefined => {
  const matchingRules = rules?.filter((rule) => rule.condition.transferTypes.includes(APPROVAL_TRANSFER_TYPES[key]));
  if (!matchingRules?.length) {
    return undefined;
  }

  return matchingRules.reduce((effectiveRule, rule) => {
    if (effectiveRule.condition.amountGte === undefined) {
      return effectiveRule;
    }
    if (rule.condition.amountGte === undefined || rule.condition.amountGte < effectiveRule.condition.amountGte) {
      return rule;
    }
    return effectiveRule;
  });
};

interface BuildApprovalRulesParams {
  amounts?: ApprovalAmounts;
  conditions?: ApprovalConditions;
  existingRules?: TransferApprovalRules;
}

export const buildApprovalRules = ({
  amounts,
  conditions,
  existingRules = [],
}: BuildApprovalRulesParams): TransferApprovalRules | null => {
  const configuredTransferTypes = new Set(CONFIGURABLE_RULE_KEYS.map((key) => APPROVAL_TRANSFER_TYPES[key]));

  const preservedRules = [...existingRules]
    .sort((a, b) => a.priority - b.priority)
    .flatMap((rule) => {
      const transferTypes = rule.condition.transferTypes.filter((type) => !configuredTransferTypes.has(type));
      return transferTypes.length > 0
        ? [
            {
              ...rule,
              condition: { ...rule.condition, transferTypes },
            },
          ]
        : [];
    });

  const configuredRules = CONFIGURABLE_RULE_KEYS.flatMap((key) => {
    if (conditions?.[key] !== 'threshold') {
      return [];
    }

    return [
      {
        condition: {
          amountGte: amounts?.[key],
          transferTypes: [APPROVAL_TRANSFER_TYPES[key]],
        },
        priority: 0,
      },
    ];
  });

  const rules = [...preservedRules, ...configuredRules].map((rule, index) => ({ ...rule, priority: index + 1 }));
  return rules.length > 0 ? rules : null;
};
